import { useNavigate } from 'react-router-dom';
import { Button } from '@portal_components/components/Button';
import { DatePicker } from '@portal_components/components/DatePicker';
import { TableItem } from '@portal_components/components/TableItem';
import { TextInput } from '@portal_components/components/TextInput';
import { Group, Contract, EPageViewType } from '../../types';
import { ContractView } from '../contract/ContractView';
import { GroupView } from '../group/GroupView';
import { useContractStandingData } from '../../hooks/useContractStandingData';
import { EnvironmentVariables } from '../../EnvironmentVariables';
import { useUserInfo } from '../../hooks/useUserInfo';
import { EUserRole } from '@portal_components/types';

interface CustomerSearchResultProps {
  group: Group;
  contracts: Partial<Contract>[];
  expandedContractIndex: number | null;
  setExpandedContractIndex: (index: number | null) => void;
}

export const CustomerSearchResult = ({ group, contracts, expandedContractIndex, setExpandedContractIndex }: CustomerSearchResultProps) => {
  const navigate = useNavigate();
  const { userInfo } = useUserInfo();
  const {
    standingData: { standingDataContractTypeList }
  } = useContractStandingData();

  const isReadonlyUser = userInfo?.role === EUserRole.READONLY_USER;

  const handleViewSitesInPortfolioPortal = (contractId: string) => () => {
    if (!group.customerGroupId) return;
    const searchParams = new URLSearchParams({
      contractId,
      customerGroupId: group.customerGroupId
    });
    window.open(`${EnvironmentVariables.PortfolioPortalUrl}/search?${searchParams.toString()}`, '_blank');
  };

  const handleEditGroupButtonClick = () => {
    navigate(`/editGroup/${group.customerGroupId}`);
  };

  const handleEditContractButtonClicked = (contractId?: string) => () => {
    navigate(`/editContract/${group.customerGroupId}/${contractId}`);
  };

  const handleRenewContractButtonClicked = (contractId?: string) => () => {
    navigate(`/renewContract/${group.customerGroupId}/${contractId}`);
  };

  const handleAddContractButtonClicked = () => {
    navigate(`/addContract/${group.customerGroupId}`);
  };

  const renderContractListing = (contract: Partial<Contract>, index: number) => {
    if (!contract.contractId) return null;

    const expanded = expandedContractIndex === index;
    const handleExpandClicked = () => {
      setExpandedContractIndex(expanded ? null : index);
    };

    const contractTypeTitle = standingDataContractTypeList.find(({ value }) => value == contract.contractType)?.title ?? '';

    return (
      <div key={`CustomerSearchResultContract-${group.customerGroupId}-${contract.contractId}`} className="mb-3">
        <div className="bg-elevation-1 px-4 py-2 rounded-md">
          <TableItem readonly onExpandClicked={handleExpandClicked} expanded={expanded}>
            <TextInput title="Contract" initialValue={contract.contractId} readonly hideErrorControl />
            <TextInput title="Contract Type" initialValue={contractTypeTitle} readonly hideErrorControl />

            <DatePicker title="Contract Start" initialValue={contract.contractStartDate} readonly onDateSelected={() => undefined} hideErrorControl />

            <DatePicker title="Contract End" initialValue={contract.contractEndDate} readonly onDateSelected={() => undefined} hideErrorControl />
          </TableItem>
        </div>

        {expanded && (
          <div className="pl-4 mt-2 border-l border-secondary-deep-blue-80">
            <div className="pb-2 flex flex-row justify-end items-center gap-1">
              <div className="text-interactive-coral hover:text-interactive-coral-db-70 text-sm cursor-pointer" onClick={handleViewSitesInPortfolioPortal(contract.contractId)}>
                View sites in Portfolio Portal
              </div>
              <img src="/externalLinkButtonActive.svg" alt="external-link" className="w-4 h-4" />
            </div>

            <div className="bg-elevation-2 rounded-md px-4 py-3">
              <ContractView viewType={EPageViewType.VIEW} group={group} contract={contract} />

              {!isReadonlyUser && (
                <div className="flex flex-row justify-end pt-4 gap-4">
                  <Button text="Renew Contract" primary={false} onClick={handleRenewContractButtonClicked(contract.contractId)} />
                  <Button text="Edit Contract" primary onClick={handleEditContractButtonClicked(contract.contractId)} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="bg-elevation-1 rounded-md px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-solid-white">Customer details</h2>

          {!isReadonlyUser && <Button text="Edit Customer" primary onClick={handleEditGroupButtonClick} />}
        </div>

        <div className="mt-3">
          <GroupView viewType={EPageViewType.VIEW} group={group} />
        </div>
      </div>

      <div className="mt-6 mb-3 bg-elevation-1 rounded-md px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-solid-white">Contracts</h2>
            <span className="text-sm text-secondary-deep-blue-20">({contracts.length})</span>
          </div>

          {!isReadonlyUser && <Button text="Add Contract" primary onClick={handleAddContractButtonClicked} />}
        </div>
      </div>

      {contracts.length > 0 && contracts.map(renderContractListing)}
    </div>
  );
};
